import { Money, dec, localDate } from "@despejo/domain";
import { type EngineParameter, type UsedParameter, decimalField, pickParameter, usedParameter } from "./params.js";

/**
 * Impuesto sobre la renta (utilidades), régimen general: annual estimate for
 * Form 101 (persona física con actividad lucrativa) and Form 102 (persona
 * jurídica). Plan §17, docs/tax/RENTA_PERSONA_FISICA.md,
 * docs/tax/RENTA_PERSONA_JURIDICA.md.
 *
 * Pure: (ledger items, credits, parameter rows) -> traced estimate. Every
 * bracket and rate comes from parameter_versions (RENTA_PF_BRACKETS,
 * RENTA_PF_FAMILY_CREDITS, RENTA_PJ_RATES). A DRAFT parameter, a projection
 * from a partial year or an unclassified expense makes the result provisional.
 *
 * Sources: Ley 7092 arts. 1 (territorial source), 4 (calendar year), 8
 * (deductible expenses), 15 a)-c) (rates and scales), 22 (pagos parciales);
 * Reglamento 43198-H arts. 24 (salary + lucrative activity: one exempt band),
 * 26 (credits: pagos parciales and retentions).
 *
 * 1.1.0 (DES-77): a received receipt that was rejected, or whose acceptance
 * window closed without acceptance, is not deducted (Reglamento 44739-H art.
 * 20, RES-0027-2024 art. 10; conservative reading, docs/tax/RECEIPT_ACCEPTANCE.md
 * §2.2 [verify]); a foreign-currency amount without an exchange rate is left
 * out and flagged, because it cannot be converted (Ley 7092 art. 5).
 */

export const RENTA_ENGINE_VERSION = "renta@1.1.0";

export type RentaTaxpayerType = "PERSONA_FISICA" | "PERSONA_JURIDICA";

export interface RentaItem {
  /** Ledger reference: "<transaction id>" or "<transaction id>#<line no>". */
  ref: string;
  kind: "INCOME" | "EXPENSE";
  /** Amount in CRC without IVA. Credit notes carry negative amounts. */
  amount: string;
  /** EXPENSE: the line's income_tax_deductible classification (null = not classified yet). */
  deductible: boolean | null;
  businessUse?: "BUSINESS" | "PERSONAL" | "MIXED";
  /** Business share in percent when businessUse = MIXED. */
  businessPct?: string | null;
  /**
   * EXPENSE backed by a received electronic receipt that needs the receiver's
   * acceptance. NOT_ACCEPTED = rejected, or the window closed without an
   * acceptance; PENDING = the window is still open (dueDate = last day).
   */
  acceptance?: { status: "ACCEPTED" | "PENDING" | "NOT_ACCEPTED"; dueDate?: string | null } | null;
  /** Set when the amount is in a foreign currency with no exchange rate: `amount` is then unconverted and is left out. */
  missingExchangeRate?: { currency: string; amount: string } | null;
}

export interface RentaCredit {
  /** Obligation id, receipt id or a manual reference. */
  ref: string;
  amount: string;
  note?: string;
}

export interface RentaInput {
  taxpayerType: RentaTaxpayerType;
  fiscalYear: number;
  items: RentaItem[];
  /** Months of the year the ledger covers (1-12). Below 12 the estimate is projected linearly to the full year. */
  monthsCovered: number;
  /** PF only: salary from dependent employment in the same year (Reg. art. 24). */
  salary?: { monthlyGross: string; months: number } | null;
  /** PF only: family credits claimed (art. 15 c). */
  dependents?: { children: number; spouse: boolean } | null;
  /** Pagos parciales already paid for this fiscal year (Reg. art. 26). */
  partialPayments: RentaCredit[];
  /** Creditable retentions (e.g. 2% on public-sector contracts). */
  retentions: RentaCredit[];
}

export type RentaExceptionCode =
  | "PARAMETER_VERSION_MISSING"
  | "UNCLASSIFIED_EXPENSE"
  | "UNACCEPTED_RECEIPT"
  | "ACCEPTANCE_PENDING"
  | "MISSING_EXCHANGE_RATE"
  | "SALARY_BAND_INTERPRETATION"
  | "BAD_INPUT";

export interface RentaException {
  code: RentaExceptionCode;
  ref: string;
  message: string;
}

export interface BracketRow {
  from: string;
  to: string | null;
  rate: string;
  base: string;
  tax: string;
}

export type RentaTraceBucket =
  | "income"
  | "deduction"
  | "not_deductible"
  | "unclassified"
  | "missing_rate"
  | "credit:partial_payment"
  | "credit:retention"
  | "credit:family";

export interface RentaTrace {
  bucket: RentaTraceBucket;
  ref: string;
  amount: string;
  note?: string;
}

export interface RentaResult {
  engine: string;
  fiscalYear: number;
  taxpayerType: RentaTaxpayerType;
  /** False when a needed parameter is missing: no tax figure can be given. */
  computable: boolean;
  provisional: boolean;
  provisionalReasons: string[];
  parameters: UsedParameter[];
  /** Ledger sums for the months covered (traceable row by row). */
  grossIncome: string;
  deductions: string;
  notDeductible: string;
  /** Part of notDeductible: expenses whose receipt was not accepted in time. */
  notAccepted: string;
  unclassified: string;
  netIncome: string;
  /** "1" for a full year; 12 / monthsCovered otherwise. */
  projectionFactor: string;
  annualGrossIncome: string;
  annualNetIncome: string;
  taxableIncome: string;
  scale: "PF_PROGRESSIVE" | "PJ_REDUCED" | "PJ_GENERAL" | null;
  /** PF: exempt band left after the salary exempt amount (Reg. art. 24). */
  exemptBand: string | null;
  salaryExemptUsed: string | null;
  brackets: BracketRow[];
  grossTax: string;
  familyCredits: string;
  taxAfterFamilyCredits: string;
  partialPayments: string;
  retentions: string;
  /** Balance due with the annual return (never negative). */
  balanceDue: string;
  /** Excess of credits over tax (saldo a favor). */
  creditBalance: string;
  exceptions: RentaException[];
  trace: RentaTrace[];
}

interface Segment {
  from: Money;
  to: Money | null;
  rate: string;
}

const r2 = (m: Money) => m.round(2).toFixed(2);

function parseBrackets(raw: unknown): Segment[] {
  if (!Array.isArray(raw) || raw.length === 0) throw new TypeError("brackets must be a non-empty array");
  let from = Money.zero();
  const out: Segment[] = [];
  raw.forEach((b: Record<string, unknown>, i) => {
    const rate = decimalField(b, "rate");
    const upTo = b.up_to === null || b.up_to === undefined ? null : Money.of(decimalField(b, "up_to"));
    if (upTo === null && i !== raw.length - 1) throw new TypeError("only the last bracket may be open-ended");
    if (upTo !== null && upTo.cmp(from) <= 0) throw new TypeError("bracket thresholds must increase");
    out.push({ from, to: upTo, rate });
    if (upTo) from = upTo;
  });
  if (out[out.length - 1]!.to !== null) throw new TypeError("the last bracket must be open-ended");
  return out;
}

/** Tax on `income` under a progressive scale; one row per bracket touched (zero-rate rows included). */
export function applyBrackets(income: Money, segments: readonly Segment[]): { rows: BracketRow[]; tax: Money } {
  const rows: BracketRow[] = [];
  let tax = Money.zero();
  for (const s of segments) {
    if (income.cmp(s.from) <= 0) break;
    const top = s.to && s.to.cmp(income) < 0 ? s.to : income;
    const base = top.sub(s.from);
    const t = base.times(s.rate);
    tax = tax.add(t);
    rows.push({ from: r2(s.from), to: s.to ? r2(s.to) : null, rate: s.rate, base: r2(base), tax: r2(t) });
  }
  return { rows, tax };
}

/**
 * PF scale with the salary interaction of art. 15 c) / Reg. art. 24: the
 * exempt band shrinks by the salary exempt amount already used; the freed part
 * of tramo i) is taxed at the tramo ii) rate. The other thresholds stay where
 * they are ([verify] docs/tax/RENTA_PERSONA_FISICA.md §3.4).
 */
export function pfSegments(brackets: readonly Segment[], salaryExemptUsed: Money): { segments: Segment[]; band: Money } {
  const first = brackets[0]!;
  const second = brackets[1];
  if (!dec(first.rate).isZero() || !first.to || !second) throw new TypeError("PF scale must start with a zero-rate band");
  if (!salaryExemptUsed.isPositive()) return { segments: [...brackets], band: first.to };
  const band = first.to.sub(salaryExemptUsed).max(Money.zero());
  const segs: Segment[] = [];
  if (band.isPositive()) segs.push({ from: Money.zero(), to: band, rate: "0" });
  segs.push({ from: band, to: first.to, rate: second.rate });
  return { segments: [...segs, ...brackets.slice(1)], band };
}

export function computeRenta(input: RentaInput, params: readonly EngineParameter[]): RentaResult {
  const exceptions: RentaException[] = [];
  const trace: RentaTrace[] = [];
  const reasons: string[] = [];
  const used: UsedParameter[] = [];
  const yearEnd = localDate(input.fiscalYear, 12, 31);
  const isPF = input.taxpayerType === "PERSONA_FISICA";

  // ------------------------------------------------------------ ledger
  let gross = Money.zero();
  let deductions = Money.zero();
  let notDeductible = Money.zero();
  let notAccepted = Money.zero();
  let unclassified = Money.zero();
  let missingRates = 0;
  for (const it of input.items) {
    if (it.missingExchangeRate) {
      missingRates++;
      const m = it.missingExchangeRate;
      trace.push({ bucket: "missing_rate", ref: it.ref, amount: "0", note: `${m.amount} ${m.currency} sin tipo de cambio: no se incluyó` });
      exceptions.push({ code: "MISSING_EXCHANGE_RATE", ref: it.ref, message: `Monto en ${m.currency} sin tipo de cambio: no se puede convertir a colones (Ley 7092 art. 5) y quedó fuera del estimado` });
      continue;
    }
    const amt = Money.of(it.amount);
    if (it.kind === "INCOME") {
      gross = gross.add(amt);
      trace.push({ bucket: "income", ref: it.ref, amount: amt.toString() });
      continue;
    }
    if (it.businessUse === "PERSONAL") {
      notDeductible = notDeductible.add(amt);
      trace.push({ bucket: "not_deductible", ref: it.ref, amount: amt.toString(), note: "Gasto personal" });
      continue;
    }
    if (it.acceptance?.status === "NOT_ACCEPTED") {
      notDeductible = notDeductible.add(amt);
      notAccepted = notAccepted.add(amt);
      trace.push({ bucket: "not_deductible", ref: it.ref, amount: amt.toString(), note: "Comprobante sin aceptación en plazo: no deducible" });
      exceptions.push({ code: "UNACCEPTED_RECEIPT", ref: it.ref, message: "Comprobante recibido rechazado o sin aceptación en plazo: no se deduce (Reglamento 44739-H art. 20)" });
      continue;
    }
    if (it.acceptance?.status === "PENDING") {
      const until = it.acceptance.dueDate ? ` a más tardar el ${it.acceptance.dueDate}` : "";
      exceptions.push({ code: "ACCEPTANCE_PENDING", ref: it.ref, message: `Comprobante pendiente de aceptar${until}: si no se acepta en plazo deja de ser deducible` });
    }
    if (it.deductible === null) {
      unclassified = unclassified.add(amt);
      trace.push({ bucket: "unclassified", ref: it.ref, amount: amt.toString(), note: "Sin clasificar para renta: no se deduce" });
      exceptions.push({ code: "UNCLASSIFIED_EXPENSE", ref: it.ref, message: "Gasto sin clasificar para renta: no se deduce hasta clasificarlo" });
      continue;
    }
    if (!it.deductible) {
      notDeductible = notDeductible.add(amt);
      trace.push({ bucket: "not_deductible", ref: it.ref, amount: amt.toString(), note: "Clasificado como no deducible" });
      continue;
    }
    let part = amt;
    if (it.businessUse === "MIXED") {
      const pct = it.businessPct ?? "0";
      part = amt.times(dec(pct).dividedBy(100).toString());
      const rest = amt.sub(part);
      notDeductible = notDeductible.add(rest);
      trace.push({ bucket: "not_deductible", ref: it.ref, amount: rest.toString(), note: `Uso personal ${dec(100).minus(pct).toString()}%` });
    }
    deductions = deductions.add(part);
    trace.push({ bucket: "deduction", ref: it.ref, amount: part.toString(), note: it.businessUse === "MIXED" ? `Uso del negocio ${it.businessPct}%` : undefined });
  }
  const net = gross.sub(deductions);

  // --------------------------------------------------------- projection
  const months = Math.trunc(input.monthsCovered);
  if (months < 1 || months > 12) {
    exceptions.push({ code: "BAD_INPUT", ref: "monthsCovered", message: "Los meses cubiertos deben estar entre 1 y 12" });
  }
  const safeMonths = Math.min(12, Math.max(1, months || 12));
  const factor = dec(12).dividedBy(safeMonths);
  if (safeMonths < 12) reasons.push(`Proyección lineal de ${safeMonths} mes(es) a 12`);
  const annualGross = gross.times(factor);
  const annualNet = net.times(factor);
  const taxable = annualNet.isPositive() ? annualNet : Money.zero();
  if (unclassified.isPositive()) reasons.push("Hay gastos sin clasificar (no se dedujeron)");
  if (missingRates > 0) reasons.push(`${missingRates} movimiento(s) en moneda extranjera sin tipo de cambio quedaron fuera`);

  // ---------------------------------------------------------- credits
  let partial = Money.zero();
  for (const c of input.partialPayments) {
    partial = partial.add(Money.of(c.amount));
    trace.push({ bucket: "credit:partial_payment", ref: c.ref, amount: Money.of(c.amount).toString(), note: c.note });
  }
  let retentions = Money.zero();
  for (const c of input.retentions) {
    retentions = retentions.add(Money.of(c.amount));
    trace.push({ bucket: "credit:retention", ref: c.ref, amount: Money.of(c.amount).toString(), note: c.note });
  }

  const base = {
    engine: RENTA_ENGINE_VERSION,
    fiscalYear: input.fiscalYear,
    taxpayerType: input.taxpayerType,
    parameters: used,
    grossIncome: r2(gross),
    deductions: r2(deductions),
    notDeductible: r2(notDeductible),
    notAccepted: r2(notAccepted),
    unclassified: r2(unclassified),
    netIncome: r2(net),
    projectionFactor: factor.toDecimalPlaces(6).toString(),
    annualGrossIncome: r2(annualGross),
    annualNetIncome: r2(annualNet),
    taxableIncome: r2(taxable),
    partialPayments: r2(partial),
    retentions: r2(retentions),
    exceptions,
    trace,
  };

  const missing = (key: string): RentaResult => {
    exceptions.push({ code: "PARAMETER_VERSION_MISSING", ref: key, message: `No hay parámetro ${key} vigente para ${input.fiscalYear}` });
    return {
      ...base,
      computable: false,
      provisional: true,
      provisionalReasons: [...reasons, `Falta el parámetro ${key}`],
      scale: null,
      exemptBand: null,
      salaryExemptUsed: null,
      brackets: [],
      grossTax: "0.00",
      familyCredits: "0.00",
      taxAfterFamilyCredits: "0.00",
      balanceDue: "0.00",
      creditBalance: "0.00",
    };
  };

  // ------------------------------------------------------------- scale
  let scale: RentaResult["scale"];
  let segments: Segment[];
  let band: Money | null = null;
  let salaryExempt: Money | null = null;
  let family = Money.zero();
  if (isPF) {
    const p = pickParameter(params, "RENTA_PF_BRACKETS", yearEnd);
    if (!p) return missing("RENTA_PF_BRACKETS");
    used.push(usedParameter(p));
    const brackets = parseBrackets(p.value.brackets);
    salaryExempt = Money.zero();
    if (input.salary && dec(input.salary.monthlyGross).gt(0) && input.salary.months > 0) {
      const cap = Money.of(decimalField(p.value, "salary_monthly_exempt"));
      salaryExempt = Money.of(input.salary.monthlyGross).min(cap).times(Math.min(12, Math.trunc(input.salary.months)));
      exceptions.push({
        code: "SALARY_BAND_INTERPRETATION",
        ref: "salary",
        message: "Con salario, el tramo exento se reduce (Reg. art. 24); la parte liberada tributa al 10% y los demás tramos no se desplazan [verificar con el formulario 101]",
      });
      reasons.push("Interpretación de tramos con salario por verificar");
    }
    const pf = pfSegments(brackets, salaryExempt);
    segments = pf.segments;
    band = pf.band;
    scale = "PF_PROGRESSIVE";
    const dep = input.dependents;
    if (dep && (dep.children > 0 || dep.spouse)) {
      const fc = pickParameter(params, "RENTA_PF_FAMILY_CREDITS", yearEnd);
      if (!fc) return missing("RENTA_PF_FAMILY_CREDITS");
      used.push(usedParameter(fc));
      const child = Money.of(decimalField(fc.value, "per_child_annual")).times(Math.max(0, Math.trunc(dep.children)));
      const spouse = dep.spouse ? Money.of(decimalField(fc.value, "spouse_annual")) : Money.zero();
      family = child.add(spouse);
      if (child.isPositive()) trace.push({ bucket: "credit:family", ref: "hijos", amount: child.toString(), note: `${dep.children} hijo(s)` });
      if (spouse.isPositive()) trace.push({ bucket: "credit:family", ref: "conyuge", amount: spouse.toString(), note: "Cónyuge" });
    }
  } else {
    const p = pickParameter(params, "RENTA_PJ_RATES", yearEnd);
    if (!p) return missing("RENTA_PJ_RATES");
    used.push(usedParameter(p));
    const threshold = Money.of(decimalField(p.value, "reduced_scale_gross_income_max"));
    // Art. 15 b): gross income of the period "no supere" the threshold -> reduced scale.
    if (annualGross.cmp(threshold) <= 0) {
      segments = parseBrackets(p.value.reduced_brackets);
      scale = "PJ_REDUCED";
    } else {
      segments = [{ from: Money.zero(), to: null, rate: decimalField(p.value, "general_rate") }];
      scale = "PJ_GENERAL";
    }
  }
  if (used.some((u) => u.review_status === "DRAFT")) reasons.unshift("Parámetros en borrador (DRAFT) pendientes de confirmación");

  const { rows, tax } = applyBrackets(taxable, segments);
  const grossTax = tax.round(2);
  const afterFamily = grossTax.sub(family).max(Money.zero());
  const balance = afterFamily.sub(partial).sub(retentions);

  return {
    ...base,
    computable: true,
    provisional: reasons.length > 0,
    provisionalReasons: reasons,
    scale,
    exemptBand: band ? r2(band) : null,
    salaryExemptUsed: salaryExempt ? r2(salaryExempt) : null,
    brackets: rows,
    grossTax: r2(grossTax),
    familyCredits: r2(family),
    taxAfterFamilyCredits: r2(afterFamily),
    balanceDue: r2(balance.isPositive() ? balance : Money.zero()),
    creditBalance: r2(balance.isNegative() ? balance.neg() : Money.zero()),
  };
}
